import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { BoobleModalService } from 'booble-modal';
import { NotifierService } from 'angular-notifier';
import { Moedas, MoedasInbound } from 'src/app/domain/constants/moeda.constant';
import { CountryDto } from 'src/app/domain/models/dto/country.dto';
import { AuthenticationService } from 'src/app/shared/services';
import { BankAccountService } from 'src/app/shared/services/bank-account.service';
import { CountriesService } from 'src/app/shared/services/countries.service';
import { CountryValidationRules } from 'src/app/domain/constants/country-validation.constant';
import {
  ContactAccountDto,
  EAccountBankType,
} from 'src/app/domain/models/dto/contact-account.dto';
import { noWhitespaceValidator } from 'src/app/shared/validators/form-validators';
import { MerchantService } from 'src/app/core/merchant.service';
import { TenantGeneralService } from 'src/app/core/tenant-general.service';

@Component({
  selector: 'app-add-contact',
  templateUrl: './add-contact.component.html',
  styleUrls: ['./add-contact.component.scss'],
})
export class AddContactComponent implements OnInit {
  private readonly notifier: NotifierService;

  boobleModalData: { contact: ContactAccountDto; isEdit: boolean };
  contactType: string = '';
  currency: any;
  loading: boolean = false;

  isMobile = false;
  senderForm: FormGroup;
  beneficiaryForm: FormGroup;
  moedas = Moedas;
  moedasInbound = MoedasInbound;
  countries: CountryDto[];
  countryValidationRules = CountryValidationRules;
  merchant: string = '';
  warrenGlobalOnly: boolean = false;

  constructor(
    private fb: FormBuilder,
    private modalService: BoobleModalService,
    private readonly countriesService: CountriesService,
    private readonly accountService: AuthenticationService,
    private bottomSheet: MatBottomSheet,
    notifierService: NotifierService,
    private readonly authService: AuthenticationService,
    private readonly bankAccountService: BankAccountService,
    private merchantService: MerchantService,
    private tenantService: TenantGeneralService
  ) {
    this.notifier = notifierService;
  }

  get isCurrencyUnchanged(): boolean {
    const selected = this.senderForm.get('currency')?.value;
    return selected?.id === this.currency?.id;
  }

  get companyId() {
    return +this.authService.getCompanyId();
  }

  ngOnInit(): void {
    this.merchant = this.merchantService.getUserMerchant();

    this.warrenGlobalOnly =
      this.merchant == this.tenantService.WARREN_GLOBAL_NAME;
    this.currency = this.boobleModalData?.contact?.currency || {
      id: 100,
      value: '',
      description: '',
      image: 'assets/flags/mxn.svg',
      icon: 'mxn',
      selected: false,
    };

    this.isMobile = window.innerWidth <= 820;
    this.initializeForms();

    if (this.warrenGlobalOnly) {
      this.beneficiaryForm
        .get('fcc')
        ?.setValidators([Validators.required, noWhitespaceValidator]);
      this.beneficiaryForm.get('fcc')?.updateValueAndValidity();
    }

    this.getCountries().then(() => {
      if (this.boobleModalData?.isEdit && this.boobleModalData?.contact) {
        const contact = this.boobleModalData.contact;
        this.contactType =
          contact.type === EAccountBankType.SenderAccount
            ? 'SENDER'
            : 'BENEFICIARY';

        if (this.contactType === 'SENDER') {
          const country = this.countries.find(
            (c) => c.id === contact.country.id
          );
          const currency = this.moedasInbound.find(
            (m) => m.id === contact.currency.id
          );

          this.senderForm.patchValue({
            senderReceiverName: contact.senderReceiverName,
            email: contact.email,
            country: country,
            currency: currency,
          });
        } else {
          const country = this.countries.find(
            (c) => c.id === contact.country.id
          );
          const currency = this.moedas.find(
            (m) => m.id === contact.currency.id
          );

          this.beneficiaryForm.patchValue({
            senderReceiverName: contact.senderReceiverName,
            email: contact.email,
            country: country,
            currency: currency,
            name: contact.name,
            swift: contact.swift,
            number: contact.number,
            aba: contact.aba,
            iban: contact.iban,
            fcc: contact.fcc,
            blz: contact.blz,
            cbu: contact.cbu,
            cuit: contact.cuit,
            bsb: contact.bsb,
            institutionNumber: contact.institutionNumber,
            transit: contact.transit,
            address: contact.address,
            cable: contact.cable,
            sortCode: contact.sortCode,
            type: contact.type,
            isDefault: contact.isDefault,
          });
        }
      }
    });
  }

  private initializeForms(): void {
    this.senderForm = this.fb.group({
      senderReceiverName: ['', [Validators.required, noWhitespaceValidator]],
      email: ['', [Validators.required, Validators.email]],
      country: ['', Validators.required],
      currency: [
        {
          id: 100,
          value: '',
          description: '',
          image: 'assets/flags/mxn.svg',
          icon: 'mxn',
          selected: false,
        },
        Validators.required,
      ],
    });

    this.beneficiaryForm = this.fb.group({
      senderReceiverName: ['', [Validators.required, noWhitespaceValidator]],
      email: ['', [Validators.required, Validators.email]],
      country: ['', Validators.required],
      currency: [
        {
          id: 1,
          value: 'USD',
          description: 'USD',
          image: 'assets/flags/usd.svg',
          icon: 'usd',
        },
        Validators.required,
      ],
      name: ['', Validators.required],
      swift: ['', Validators.required],
      number: ['', Validators.required],
      aba: [''],
      iban: [''],
      blz: [''],
      fcc: [''],
      cbu: [''],
      cuit: [''],
      bsb: [''],
      institutionNumber: [''],
      transit: [''],
      address: [''],
      cable: [''],
      sortCode: [''],
      type: [EAccountBankType.ReceiverAccount],
      isDefault: [false],
    });

    this.beneficiaryForm.get('country')?.valueChanges.subscribe((country) => {
      if (country) {
        this.updateFormValidators(this.beneficiaryForm, country);
      }
    });
  }

  updateFormValidators(form: FormGroup, country: CountryDto): void {
    if (!country) return;

    const rules = this.countryValidationRules[country.code.toString()];
    if (!rules) return;

    Object.keys(form.controls).forEach((controlName) => {
      const control = form.get(controlName);
      if (control) {
        const baseRequiredFields = [
          'senderReceiverName',
          'email',
          'country',
          'currency',
          'swift',
          'name',
        ];

        if (baseRequiredFields.includes(controlName)) {
          control.setValidators([Validators.required, noWhitespaceValidator]);
        } else if (rules.requiredFields.includes(controlName)) {
          control.setValidators([Validators.required, noWhitespaceValidator]);
        } else if (rules.optionalFields.includes(controlName)) {
          control.clearValidators();
        } else {
          control.clearValidators();
        }

        control.updateValueAndValidity({ emitEvent: false });
      }
    });
  }

  shouldShowField(form: FormGroup, fieldName: string): boolean {
    const country = form.get('country')?.value;
    if (!country) return false;

    const rules = this.countryValidationRules[country.code.toString()];
    if (!rules) return false;

    const baseFields = [
      'senderReceiverName',
      'email',
      'country',
      'currency',
      'swift',
      'name',
      'fcc',
    ];
    if (baseFields.includes(fieldName)) return true;

    return (
      rules.requiredFields.includes(fieldName) ||
      rules.optionalFields.includes(fieldName)
    );
  }

  isFieldRequired(form: FormGroup, fieldName: string): boolean {
    const country = form.get('country')?.value;
    if (!country) return false;

    const rules = this.countryValidationRules[country.id.toString()];
    if (!rules) return false;

    const baseRequiredFields = [
      'senderReceiverName',
      'email',
      'country',
      'currency',
      'swift',
      'name',
    ];
    if (baseRequiredFields.includes(fieldName)) return true;

    return rules.requiredFields.includes(fieldName);
  }

  getCountryRules(country: CountryDto): any {
    if (!country) return null;
    return this.countryValidationRules[country.code.toString()];
  }

  private getFilteredFormValue(form: FormGroup): any {
    const formValue = form.value;
    return Object.keys(formValue).reduce((acc, key) => {
      const value = formValue[key];
      if (
        value !== null &&
        value !== undefined &&
        value !== '' &&
        value !== false
      ) {
        acc[key] = value;
      }
      return acc;
    }, {});
  }

  private handleSuccess(message: string): void {
    this.loading = false;
    this.notifier.notify('success', message);
    this.bankAccountService.updateBankAccountsView();
    this.closeModal();
  }

  private handleError(): void {
    this.loading = false;
  }

  private saveContact(
    contactAccount: ContactAccountDto,
    isEdit: boolean
  ): void {
    this.loading = true;
    const message = isEdit
      ? `${
          contactAccount.type === EAccountBankType.SenderAccount
            ? 'Pagador'
            : 'Beneficiário'
        } atualizado com sucesso!`
      : `${
          contactAccount.type === EAccountBankType.SenderAccount
            ? 'Pagador'
            : 'Beneficiário'
        } adicionado com sucesso!`;

    if (isEdit) {
      contactAccount.id = this.boobleModalData.contact.id;
      this.bankAccountService.editBankAccount(contactAccount).subscribe({
        next: () => this.handleSuccess(message),
        error: () => this.handleError(),
      });
    } else {
      this.bankAccountService.addBankAccount(contactAccount).subscribe({
        next: () => this.handleSuccess(message),
        error: () => this.handleError(),
      });
    }
  }

  onSubmit(): void {
    if (this.contactType === 'SENDER') {
      if (this.senderForm.valid) {
        const filteredValue = this.getFilteredFormValue(this.senderForm);
        const contactAccount: ContactAccountDto = {
          ...filteredValue,
          type: EAccountBankType.SenderAccount,
        };

        this.saveContact(contactAccount, this.boobleModalData?.isEdit);
      } else {
        this.senderForm.markAllAsTouched();
      }
    } else if (this.contactType === 'BENEFICIARY') {
      if (this.beneficiaryForm.valid) {
        const filteredValue = this.getFilteredFormValue(this.beneficiaryForm);
        const contactAccount: ContactAccountDto = {
          ...filteredValue,
          type: EAccountBankType.ReceiverAccount,
        };

        this.saveContact(contactAccount, this.boobleModalData?.isEdit);
      } else {
        this.beneficiaryForm.markAllAsTouched();
      }
    }
  }

  getCountries() {
    return new Promise<void>((resolve) => {
      this.countriesService.get().subscribe((getResult) => {
        this.countries = getResult.data;
        resolve();
      });
    });
  }

  closeModal(): void {
    this.isMobile
      ? this.bottomSheet.dismiss()
      : this.modalService.dismiss(null);
  }
}
